import React, { FC, useMemo } from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import { CardVariant, CardSize, ContentCard } from "components/ui";
import { extractItem } from "components/ui/ContentCard/utils";
import { PageLoading, PageError } from "../page-container";
import { Carousel } from "../carousel";
import { LearnerArticle } from "../../api/types";
import styles from "./carousel-list.module.scss";

interface CarouselListProps {
  items: LearnerArticle[];
  listName: string;
  loading?: boolean;
  error?: boolean;
  className?: string;
  variant?: CardVariant;
  size?: CardSize;
}

const CARD_OFFSET_WIDTH = 276; // Width of card is 260px, plus 1rem flex gap

const CarouselList: FC<CarouselListProps> = ({
  items,
  listName,
  loading = false,
  error = false,
  className,
  variant = CardVariant.IMAGE,
  size = CardSize.LARGE,
}) => {
  const { t } = useTranslation(["components"]);

  // Group articles by pillar label
  const groupedByPillar = useMemo(() => {
    const groups: Record<string, LearnerArticle[]> = {};

    items.forEach((item) => {
      // Use pillar label for grouping
      const pillar = item.pillarLabel || "OTHER";
      if (!groups[pillar]) {
        groups[pillar] = [];
      }
      groups[pillar].push(item);
    });

    // Convert to array format for easier rendering
    return Object.entries(groups).map(([pillar, articles]) => ({
      pillar,
      articles,
      displayName:
        pillar.charAt(0).toUpperCase() + pillar.slice(1).toLowerCase(),
    }));
  }, [items]);

  const cardItems = useMemo(() => {
    return items.map(extractItem);
  }, [items]);

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return <PageError />;
  }

  return (
    <div className={clsx(styles["carousel-list"], className)}>
      {cardItems.length === 0 && (
        <div className={styles["no-results"]}>
          <h2>{t("components:contentLibrary.emptyTitle")}</h2>
          <p>{t("components:carouselList.emptySubtitle")}</p>
        </div>
      )}

      {cardItems.length > 0 && (
        <div className={styles["pillar-grouped-content"]}>
          {groupedByPillar.map(({ pillar, articles, displayName }) => {
            if (articles.length === 0) return null;

            const articleCards = articles.map((article, index) => {
              const cardItem = extractItem(article);
              return (
                <ContentCard
                  key={cardItem.id}
                  item={cardItem}
                  variant={variant}
                  size={size}
                  listName={`${listName}-${pillar}`}
                  listPosition={index + 1}
                />
              );
            });

            return (
              <div key={pillar} className={styles["pillar-group"]}>
                <Carousel
                  title={<h2 className={styles.title}>{displayName}</h2>}
                  items={articleCards}
                  offsetOverride={CARD_OFFSET_WIDTH}
                  showCarouselButtons
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CarouselList;
